/**
 * Protective Put API service.
 *
 * All calls go through {@link apiClient} with paths that omit the `/api`
 * prefix (the base URL already includes `/api` via runtime config), matching
 * `trade-reports-service.ts`.
 *
 * Validates: Requirements 1.1, 2.1, 7.1, 8.4
 */

import type {
  AddProtectivePutInput,
  ProtectivePutEntryInput,
  PutHistoryEntry,
  PutOutcomeInput,
  TradePosition,
} from '@covered-calls/shared';
import { apiClient } from './api-client';

/**
 * Create a protective-put position via combined entry (buy stock + buy a long
 * put in one transaction).
 */
export async function createProtectivePut(
  input: ProtectivePutEntryInput
): Promise<TradePosition> {
  return apiClient.post<TradePosition>('/trades/protective-put', input);
}

/**
 * Attach a protective put to an existing open stock holding.
 */
export async function addProtectivePut(
  positionId: string,
  input: AddProtectivePutInput
): Promise<TradePosition> {
  return apiClient.post<TradePosition>(`/trades/${positionId}/protective-put`, input);
}

/**
 * Record the end-of-life outcome of an active protective put
 * (expired / closed / exercised).
 */
export async function recordPutOutcome(
  positionId: string,
  input: PutOutcomeInput
): Promise<TradePosition> {
  return apiClient.post<TradePosition>(
    `/trades/${positionId}/protective-put/outcome`,
    input
  );
}

/**
 * Fetch the recorded protective-put history for a position.
 */
export async function getPutHistory(positionId: string): Promise<PutHistoryEntry[]> {
  return apiClient.get<PutHistoryEntry[]>(`/trades/${positionId}/put-history`);
}
